import type { ExternalStatus, Order } from "@/api/order/_models/Order";

/** Everything one system knows about an order: its reference for it and what it last reported. */
export interface OrderListSystemSummary {
  system: string;
  /** The number the system quotes (#1006, SO-00009, a PO). */
  reference?: string;
  status?: ExternalStatus;
}

const FLOW_ORDER = ["CIN7", "SHOPIFY", "TOLL", "ESTORE", "SPS", "TOE"];

const flowRank = (system: string, origin: string | undefined): number => {
  if (system === origin) return -1;
  const rank = FLOW_ORDER.indexOf(system);
  if (rank === -1) return FLOW_ORDER.length;
  return rank;
};

/**
 * One summary per system an order touches — origin first, then the flow order — merged from its
 * references, the sale's statuses and the retailer order's statuses (TOE, which takes the retailer
 * order's reference). If a system reports at both grains, the sale's status wins.
 */
export const toOrderListSystemSummaryList = (order: Order): OrderListSystemSummary[] => {
  const bySystem = new Map<string, OrderListSystemSummary>();

  const entryFor = (system: string): OrderListSystemSummary => {
    let entry = bySystem.get(system);
    if (!entry) {
      entry = { system };
      bySystem.set(system, entry);
    }
    return entry;
  };

  for (const reference of order.externalReferences) {
    const entry = entryFor(reference.source);
    if (!entry.reference) entry.reference = reference.externalReference;
  }

  for (const status of order.externalStatuses) {
    const entry = entryFor(status.system);
    if (!entry.status) entry.status = status;
  }

  const customerOrder = order.customerOrder;
  if (customerOrder) {
    for (const status of customerOrder.externalStatuses) {
      const entry = entryFor(status.system);
      if (!entry.status) entry.status = status;
      if (!entry.reference) entry.reference = customerOrder.reference;
    }
  }

  const summaries = [...bySystem.values()];
  summaries.sort((a, b) => {
    const rankDifference = flowRank(a.system, order.source) - flowRank(b.system, order.source);
    if (rankDifference !== 0) return rankDifference;
    return a.system.localeCompare(b.system);
  });
  return summaries;
};
